import { get } from '@api/api-client';
import { deleteNomination } from '@api/nominations-api';
import {
  appendPaginationQuery,
  type PaginatedResponse,
  type PaginationParams,
} from '@api/pagination';
import { updateParticipant, type UpdateParticipantDTO } from '@api/participants-api';

interface NomineeParticipantResponse {
  id: string;
  name: string;
  surname: string | null;
  photoUrl: string | null;
  profileUrl: string | null;
  city: string | null;
  dpDoctorId: number;
}

interface NomineeCategoryResponse {
  id: string;
  name: string;
  isClinic: boolean;
}

export interface NomineeDetailResponse {
  id: string;
  participant: NomineeParticipantResponse;
  editionCategoryId: string;
  category: NomineeCategoryResponse;
  voteCount: number;
  ranking: number | null;
  isWinner: boolean;
  hasVotedInEdition: boolean;
}

/** Fetches nominees for a specific edition with pagination. */
export function getNomineesByEdition(
  editionId: string,
  params?: PaginationParams,
): Promise<PaginatedResponse<NomineeDetailResponse>> {
  return get<PaginatedResponse<NomineeDetailResponse>>(
    appendPaginationQuery(`/editions/${editionId}/nominees`, params),
  );
}

/** Fetches every nominees page for an edition. */
export async function getAllNomineesByEdition(editionId: string): Promise<NomineeDetailResponse[]> {
  const pageSize = 100;
  const firstPage = await getNomineesByEdition(editionId, { page: 1, pageSize });
  const nominees = [...firstPage.data];

  for (let page = 2; page <= firstPage.meta.totalPages; page++) {
    const nextPage = await getNomineesByEdition(editionId, { page, pageSize });
    nominees.push(...nextPage.data);
  }

  return nominees;
}

export interface NomineesByCategoryResponse {
  data: NomineeDetailResponse[];
  meta: PaginatedResponse<NomineeDetailResponse>['meta'];
  editionCategoryId: string | null;
  categoryName: string | null;
}

/** Fetches nominees for a specific edition and category with pagination and context. */
export function getNomineesByCategory(
  editionId: string,
  categoryId: string,
  params?: PaginationParams,
): Promise<NomineesByCategoryResponse> {
  return get<NomineesByCategoryResponse>(
    appendPaginationQuery(`/editions/${editionId}/categories/${categoryId}/nominees`, params),
  );
}

export type AllNomineesByCategoryResponse = Omit<NomineesByCategoryResponse, 'meta'>;

/** Fetches every nominees page for an edition and category, with its context. */
export async function getAllNomineesByCategory(
  editionId: string,
  categoryId: string,
): Promise<AllNomineesByCategoryResponse> {
  const pageSize = 100;
  const firstPage = await getNomineesByCategory(editionId, categoryId, { page: 1, pageSize });
  const nominees = [...firstPage.data];

  for (let page = 2; page <= firstPage.meta.totalPages; page++) {
    const nextPage = await getNomineesByCategory(editionId, categoryId, { page, pageSize });
    nominees.push(...nextPage.data);
  }

  return {
    data: nominees,
    editionCategoryId: firstPage.editionCategoryId,
    categoryName: firstPage.categoryName,
  };
}

export interface NominationHistoryEntry {
  nominationId: string;
  editionId: string;
  editionName: string;
  editionYear: number;
  categoryId: string;
  categoryName: string;
  voteCount: number;
  ranking: number | null;
  isWinner: boolean;
}

/** Fetches the full nomination history for a participant across all editions. */
export function getNominationHistory(participantId: string): Promise<NominationHistoryEntry[]> {
  return get<NominationHistoryEntry[]>(`/participants/${participantId}/nomination-history`);
}

/** Updates the participant info associated with a nominee. */
export async function updateNomineeParticipant(
  participantId: string,
  data: UpdateParticipantDTO,
): ReturnType<typeof updateParticipant> {
  return updateParticipant(participantId, data);
}

/** Deletes a nomination by ID. */
export function deleteNominee(nominationId: string): Promise<void> {
  return deleteNomination(nominationId);
}
